/**
 * Format-driven segment model behind `DateField` / `TimeField`.
 *
 * MUI X's fields are a single `<input type="text">` whose caret snaps between
 * masked sections; consumer tests assert `getByRole('textbox')` and drive the
 * control with raw `change` events, so the replacement has to stay one input
 * rather than the multi-element accessible DOM. Everything here is pure so the
 * keyboard semantics can be tested without rendering.
 */

export type SegmentToken = 'dd' | 'MM' | 'yyyy' | 'yy' | 'HH' | 'mm' | 'ss'

export type ValidationError = 'invalidDate' | 'minDate' | 'maxDate'

type SegmentSpec = {
  length: number
  min: number
  max: number
}

const SEGMENT_SPECS: Record<SegmentToken, SegmentSpec> = {
  dd: { length: 2, min: 1, max: 31 },
  MM: { length: 2, min: 1, max: 12 },
  yyyy: { length: 4, min: 1, max: 9999 },
  yy: { length: 2, min: 0, max: 99 },
  HH: { length: 2, min: 0, max: 23 },
  mm: { length: 2, min: 0, max: 59 },
  ss: { length: 2, min: 0, max: 59 },
}

const TOKEN_PATTERN = /yyyy|yy|dd|MM|HH|mm|ss/g

const MIN_FOUR_DIGIT_YEAR = 1000

export type SegmentPart = {
  kind: 'segment'
  token: SegmentToken
  /** Index into the `sections` array, not into the rendered text. */
  index: number
}

export type LiteralPart = {
  kind: 'literal'
  text: string
}

export type FormatPart = SegmentPart | LiteralPart

/** `null` = that segment has not been filled yet. */
export type Sections = (number | null)[]

/**
 * Splits `dd/MM/yyyy` into ordered segment + literal parts. The literals are
 * kept so the rendered text and the caret offsets stay derivable from one place.
 */
export function parseFormat(format: string): FormatPart[] {
  const parts: FormatPart[] = []
  let cursor = 0
  let segmentIndex = 0

  for (const match of format.matchAll(TOKEN_PATTERN)) {
    const start = match.index

    if (start > cursor) {
      parts.push({ kind: 'literal', text: format.slice(cursor, start) })
    }

    parts.push({ kind: 'segment', token: match[0] as SegmentToken, index: segmentIndex })
    segmentIndex += 1
    cursor = start + match[0].length
  }

  if (cursor < format.length) {
    parts.push({ kind: 'literal', text: format.slice(cursor) })
  }

  return parts
}

export function getSegments(parts: FormatPart[]): SegmentPart[] {
  return parts.filter((part): part is SegmentPart => part.kind === 'segment')
}

const PLACEHOLDERS: Record<SegmentToken, string> = {
  dd: 'DD',
  MM: 'MM',
  yyyy: 'YYYY',
  yy: 'YY',
  HH: 'HH',
  // Not `MM` — uppercasing the token would render minutes identically to months.
  mm: 'mm',
  ss: 'ss',
}

function renderSegment(token: SegmentToken, value: number | null): string {
  if (value == null) {
    return PLACEHOLDERS[token]
  }

  return String(value).padStart(SEGMENT_SPECS[token].length, '0')
}

/** The masked string shown in the input — unfilled segments render as `DD`, `MM`, … */
export function formatSections(parts: FormatPart[], sections: Sections): string {
  return parts
    .map((part) =>
      part.kind === 'literal' ? part.text : renderSegment(part.token, sections[part.index] ?? null),
    )
    .join('')
}

/** Caret range of one segment inside the string `formatSections` produces. */
export function getSegmentRange(
  parts: FormatPart[],
  sections: Sections,
  segmentIndex: number,
): { start: number; end: number } {
  let offset = 0

  for (const part of parts) {
    const text =
      part.kind === 'literal' ? part.text : renderSegment(part.token, sections[part.index] ?? null)

    if (part.kind === 'segment' && part.index === segmentIndex) {
      return { start: offset, end: offset + text.length }
    }

    offset += text.length
  }

  return { start: 0, end: 0 }
}

/** Which segment the caret at `caret` belongs to — used for click-to-select. */
export function getSegmentAtCaret(
  parts: FormatPart[],
  sections: Sections,
  caret: number,
): number | null {
  const segments = getSegments(parts)

  for (const segment of segments) {
    const { start, end } = getSegmentRange(parts, sections, segment.index)

    // `<= end` so a caret resting on the trailing edge keeps the segment it just left.
    if (caret >= start && caret <= end) {
      return segment.index
    }
  }

  // Caret inside a multi-character literal (`dd - MM`): pick the nearest segment
  // rather than always falling through to the last one.
  let nearest: number | null = null
  let bestDistance = Number.POSITIVE_INFINITY

  for (const segment of segments) {
    const { start, end } = getSegmentRange(parts, sections, segment.index)
    const distance = caret < start ? start - caret : caret - end

    if (distance < bestDistance) {
      bestDistance = distance
      nearest = segment.index
    }
  }

  return nearest
}

/**
 * Reads free text back into sections, in order, one digit-run per segment.
 *
 * This is the path a raw `change` event takes (paste, autofill, and the
 * `fireEvent.change(el, { value: '09:' })` in the fairplay tests). Runs longer
 * than the segment keep their trailing digits so `1234` lands as `12`/`34`.
 */
export function parseTextIntoSections(text: string, parts: FormatPart[]): Sections {
  const segments = getSegments(parts)
  const sections: Sections = segments.map(() => null)
  const groups = text.match(/\d+/g)

  if (!groups) {
    return sections
  }

  let segmentIndex = 0

  for (const group of groups) {
    let rest = group

    while (rest.length > 0 && segmentIndex < segments.length) {
      const segment = segments[segmentIndex]

      if (!segment) {
        break
      }

      const { length } = SEGMENT_SPECS[segment.token]
      const chunk = rest.slice(0, length)

      sections[segmentIndex] = Number(chunk)
      segmentIndex += 1
      rest = rest.slice(length)
    }
  }

  return sections
}

/**
 * Types one digit into a segment.
 *
 * `pending` is the digits already typed into this segment during the current
 * run; it is what makes `1` then `2` mean December rather than two separate
 * months. Returns `complete` when the segment can take no further digit, so the
 * caller knows to advance.
 */
export function typeDigit(
  token: SegmentToken,
  pending: string,
  digit: string,
): { value: number; pending: string; complete: boolean } {
  const spec = SEGMENT_SPECS[token]
  const candidate = `${pending}${digit}`.slice(-spec.length)
  const numeric = Number(candidate)

  // A leading digit too large to extend (e.g. `4` for a month) is its own answer,
  // but a lone `0` for a 1-based segment must wait for its second digit.
  const cannotExtend = numeric * 10 > spec.max
  const complete = candidate.length >= spec.length || (cannotExtend && numeric >= spec.min)

  if (numeric > spec.max) {
    const restarted = Number(digit)
    const restartedComplete = restarted * 10 > spec.max

    return {
      value: restarted,
      pending: restartedComplete ? '' : digit,
      complete: restartedComplete,
    }
  }

  return { value: numeric, pending: complete ? '' : candidate, complete }
}

/**
 * Arrow up/down on a segment, wrapping at the segment's own bounds.
 *
 * `upperBound` narrows the wrap point for the day segment once the month is
 * known, so stepping past 28 February lands on 1 rather than walking through
 * 29/30/31 as invalid dates.
 */
export function stepSegment(
  token: SegmentToken,
  value: number | null,
  delta: number,
  fallback: number,
  upperBound?: number,
): number {
  const spec = SEGMENT_SPECS[token]
  const max = Math.min(spec.max, upperBound ?? spec.max)

  if (value == null) {
    return Math.min(Math.max(fallback, spec.min), max)
  }

  const span = max - spec.min + 1
  const next = value + delta - spec.min

  return (((next % span) + span) % span) + spec.min
}

/** Days in the given 1-based month, or `undefined` when either part is unknown. */
export function getDaysInMonth(year: number | null, month: number | null): number | undefined {
  if (month == null) {
    return undefined
  }

  // Day 0 of the next month is the last day of this one. February without a year
  // is treated as a leap month so a valid 29 is never unreachable.
  return new Date(year ?? 2024, month, 0).getDate()
}

const UNIT_BY_TOKEN: Record<
  SegmentToken,
  'year' | 'month' | 'day' | 'hours' | 'minutes' | 'seconds'
> = {
  yyyy: 'year',
  yy: 'year',
  MM: 'month',
  dd: 'day',
  HH: 'hours',
  mm: 'minutes',
  ss: 'seconds',
}

/**
 * Composes filled sections into a `Date`, using `reference` for the units the
 * format does not carry (a `HH:mm` field keeps its date, a `dd/MM/yyyy` field
 * keeps its time).
 *
 * Returns `null` for anything not fully filled, and for values the calendar
 * would silently roll — Feb 31, or a wall time inside a DST gap. Both are
 * caught by rebuilding the date and checking the units survived, which is why
 * this reports `invalidDate` instead of quietly returning the rolled date.
 *
 * A `yyyy` segment under four digits counts as not fully filled. `2`, `20` and
 * `202` are the prefixes of `2025`, not the years 2, 20 and 202, so the mask
 * must not hand a consumer a date the user is still typing.
 */
export function sectionsToDate(
  parts: FormatPart[],
  sections: Sections,
  reference: Date,
): Date | null {
  const segments = getSegments(parts)

  if (segments.length === 0 || sections.some((section) => section == null)) {
    return null
  }

  const units = {
    year: reference.getFullYear(),
    month: reference.getMonth() + 1,
    day: reference.getDate(),
    hours: reference.getHours(),
    minutes: reference.getMinutes(),
    seconds: reference.getSeconds(),
  }

  for (const segment of segments) {
    const value = sections[segment.index]

    if (value == null) {
      return null
    }

    if (segment.token === 'yyyy' && value < MIN_FOUR_DIGIT_YEAR) {
      return null
    }

    units[UNIT_BY_TOKEN[segment.token]] = segment.token === 'yy' ? 2000 + value : value
  }

  const candidate = new Date(
    units.year,
    units.month - 1,
    units.day,
    units.hours,
    units.minutes,
    units.seconds,
    0,
  )

  if (Number.isNaN(candidate.getTime())) {
    return null
  }

  // Only the units this format carries are checked. Asserting the inherited ones
  // too would reject legitimate dates in zones where the reference wall time does
  // not exist — local midnight on a DST-start day in America/Santiago, Asia/Beirut
  // or Africa/Cairo, which would otherwise make that date unenterable.
  const actual = {
    year: candidate.getFullYear(),
    month: candidate.getMonth() + 1,
    day: candidate.getDate(),
    hours: candidate.getHours(),
    minutes: candidate.getMinutes(),
    seconds: candidate.getSeconds(),
  }

  const survived = segments.every(
    (segment) => actual[UNIT_BY_TOKEN[segment.token]] === units[UNIT_BY_TOKEN[segment.token]],
  )

  return survived ? candidate : null
}

/**
 * Reference supplying the units a format omits when there is no current value.
 *
 * Everything finer than the format's smallest token is zeroed, so typing the
 * same value twice always yields the same `Date`: a `dd/MM/yyyy` mask resolves
 * to midnight, and an `HH:mm` mask to :00 seconds rather than inheriting
 * whatever second it happened to be.
 */
export function getDefaultReference(parts: FormatPart[], now: Date = new Date()): Date {
  const tokens = new Set(getSegments(parts).map((segment) => segment.token))

  if (tokens.has('ss')) {
    return now
  }

  if (tokens.has('mm')) {
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      now.getHours(),
      now.getMinutes(),
      0,
      0,
    )
  }

  if (tokens.has('HH')) {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), 0, 0, 0)
  }

  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
}

/** Splits a `Date` back into sections for the given format. */
export function dateToSections(parts: FormatPart[], date: Date | null): Sections {
  const segments = getSegments(parts)

  if (!date || Number.isNaN(date.getTime())) {
    return segments.map(() => null)
  }

  return segments.map((segment) => {
    switch (segment.token) {
      case 'yyyy':
        return date.getFullYear()
      case 'yy':
        // 2000-2099 only; `yyyy` is the format to use outside that window.
        return date.getFullYear() % 100
      case 'MM':
        return date.getMonth() + 1
      case 'dd':
        return date.getDate()
      case 'HH':
        return date.getHours()
      case 'mm':
        return date.getMinutes()
      case 'ss':
        return date.getSeconds()
    }
  })
}

/**
 * Bounds-only check for a date that did not come from a mask — a preset, or a
 * calendar click. There are no sections to consult, so the empty/partial cases
 * `validate` handles do not arise.
 */
export function validateBounds(
  date: Date | null,
  bounds: { min?: Date; max?: Date },
): ValidationError | null {
  if (!date) {
    return null
  }

  if (bounds.min && date.getTime() < bounds.min.getTime()) {
    return 'minDate'
  }

  if (bounds.max && date.getTime() > bounds.max.getTime()) {
    return 'maxDate'
  }

  return null
}

export function validate(
  date: Date | null,
  sections: Sections,
  bounds: { min?: Date; max?: Date },
): ValidationError | null {
  const isEmpty = sections.every((section) => section == null)

  if (!date) {
    return isEmpty ? null : 'invalidDate'
  }

  return validateBounds(date, bounds)
}
